import React from "react";
import { useTranslation } from "next-i18next";

import TaskCard from "@/containers/activities/rewards/TaskCard";
import { useRouter } from "next/router";
import handleDeepLink from "@/utils/handleDeepLink";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { useAvatarQuota } from "@/hooks/useRequest/useAvatarRequest";
import { claimAward, useDepositRewards } from "@/hooks/useRequest/useDepoitRewardRequest";
import { PromotionType } from "@/interfaces/depoistReward";
import { ClaimStatus } from "@/interfaces/depoistReward";
import log2Sentry from "@/utils/log2Sentry";

import Toast from "@/components/Toast";
import TeslaActivity from "./TeslaAcTivity";

enum TaskListType {
	BONUS,
	STOCK,
	INVITE,
	AVATAR,
}

export default function TaskListView({ isActivityActive }: { isActivityActive: boolean }) {
	const router = useRouter();
	const { t } = useTranslation("activities");
	const { data: avatarQuota } = useAvatarQuota();
	const { data: rewardsData, mutate } = useDepositRewards(PromotionType.DEPOSIT);

	const taskList = [
		// {
		// 	title: t("invite-friends"),
		// 	desc: t("get-commission-reward-up-to-25", { num: getPreset }),
		// 	type: TaskListType.INVITE,
		// 	icon: getFullWebCdnUrl(`/activities/deposit-rewards/rewards-invite-friend-icon-${suffix}.png`),
		// 	icon: getFullWebCdnUrl(`/activities/deposit-rewards/rewards-invite-friend-icon-${suffix}.png`),
		// },
		{
			title: t("accumulated-deposit-over-1000"),
			desc: t("get-a-free-digital-avatar"),
			type: TaskListType.AVATAR,
			iconDark: `${process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND}/h5/activities/deposit-rewards/rewards-invite-nft-icon-dark.png`,
			iconLight: `${process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND}/h5/activities/deposit-rewards/rewards-invite-nft-icon-white.png`,
		},
	];

	const getRewardStatusFromTaskType = (type: TaskListType) => {
		switch (type) {
			case TaskListType.INVITE:
				return ClaimStatus.UNABLE_TO_CLAIM;
			case TaskListType.AVATAR: {
				if (avatarQuota?.hasOwned) {
					return ClaimStatus.CLAIMED;
				}
				return avatarQuota?.canOwned ? ClaimStatus.NOT_CLAIMED : ClaimStatus.UNABLE_TO_CLAIM;
			}
			case TaskListType.BONUS:
				return rewardsData?.cashAward.status || ClaimStatus.UNABLE_TO_CLAIM;
			case TaskListType.STOCK:
				return rewardsData?.stockAward.status || ClaimStatus.UNABLE_TO_CLAIM;
			default:
				return ClaimStatus.UNABLE_TO_CLAIM;
		}
	};

	// 现金&股票奖励跳转
	const onBonusAndStockBtnClick = (status: ClaimStatus, type: TaskListType) => {
		switch (status) {
			case ClaimStatus.UNABLE_TO_CLAIM:
				// 新活动页
				const path = `${window.location.origin}/${router.locale}/activities/deposit-rewards?hidden_navigation_bar=1&status_bar=1`;
				handleDeepLink(clientDeepLinks.webview(path));
				break;
			case ClaimStatus.NOT_CLAIMED:
				// 触发领取 post
				handleCain(type);
				break;
			case ClaimStatus.CLAIMED:
			default:
				break;
		}
	};

	// 头像跳转
	const onAvatarBtnClick = (status: ClaimStatus) => {
		switch (status) {
			case ClaimStatus.UNABLE_TO_CLAIM:
				// 未达到领取 【资产页】
				handleDeepLink(clientDeepLinks.assetPage());
				break;
			case ClaimStatus.NOT_CLAIMED:
				// 头像own
				router.push("/nft/avatars");
				break;
			case ClaimStatus.CLAIMED:
			default:
				break;
		}
	};

	// 邀请跳转
	const onInviteBtnClick = () => {
		// 返佣页
		router.push("/activities/rebate-commission");
	};

	// 按钮文案
	const renderTaskCardBtnTxt = (type: TaskListType) => {
		// 根据type 获取状态
		const status = getRewardStatusFromTaskType(type);
		switch (status) {
			case ClaimStatus.UNABLE_TO_CLAIM:
				return t("do-it");
			case ClaimStatus.NOT_CLAIMED:
				return t("claim");
			case ClaimStatus.CLAIMED:
				return t("done");
		}
	};

	const onTaskCardCLick = (type: TaskListType) => {
		const status = getRewardStatusFromTaskType(type);
		switch (type) {
			case TaskListType.STOCK:
			case TaskListType.BONUS:
				onBonusAndStockBtnClick(status, type);
				break;
			case TaskListType.INVITE:
				onInviteBtnClick();
				break;
			case TaskListType.AVATAR:
				onAvatarBtnClick(status);
				break;
			default:
				break;
		}
	};

	const handleCain = async (taskType: TaskListType) => {
		try {
			let awardRecordId: number | undefined;
			if (taskType === TaskListType.BONUS) {
				awardRecordId = rewardsData?.cashAward.awardRecordId;
			} else {
				awardRecordId = rewardsData?.stockAward.awardRecordId;
			}

			if (!awardRecordId) throw Error("missing awardRecordId");
			await claimAward(awardRecordId);
			mutate();
			if (taskType === TaskListType.STOCK) {
				Toast.info(t("stock-rewards-will-be-distributed-within-7-business-days"));
			}
		} catch (error: any) {
			log2Sentry(error.message, "error");
		}
	};

	return (
		<div className="mt-8 px-4">
			{taskList.map((task, key) => (
				<TaskCard
					key={key}
					title={task.title}
					desc={task.desc}
					btnText={renderTaskCardBtnTxt(task.type)}
					done={getRewardStatusFromTaskType(task.type) === ClaimStatus.CLAIMED}
					iconDark={task.iconDark}
					iconLight={task.iconLight}
					onClick={() => onTaskCardCLick(task.type)}
				/>
			))}

			{isActivityActive && <TeslaActivity />}
		</div>
	);
}
